<template>
  <dx-tree-list
    id="mped-plan-dx-tree-list"
    ref="treeListRef"
    :allow-column-resizing="true"
    :column-chooser="{ enabled: true, mode: 'select' }"
    :column-min-width="70"
    :data-source="dataSource"
    :filter-panel="{ visible: true }"
    :filter-row="{ visible: true }"
    :header-filter="{ visible: true }"
    :hover-state-enabled="true"
    :show-borders="true"
    :show-column-lines="true"
    :show-row-lines="true"
    class="flex-grow-1"
    column-resizing-mode="widget"
    filter-mode="fullBranch"
    height="50px"
    item-template="treeListItemTemplate"
    key-expr="kod_spr_smet"
    parent-id-expr="kod_parent"
    width="100%"
    @toolbar-preparing="onToolbarPreparing($event)"
    @content-ready="onContentReady($event)"
  >
    <dx-load-panel :enabled="true" />
    <dx-scrolling mode="standart" />
    <dx-sorting mode="single" />

    <dx-column
      :visible="false"
      caption="ID"
      dataField="kod_spr_smet"
      width="200px"
    ></dx-column>

    <dx-column
      caption="Наименование"
      dataField="name"
      dataType="string"
    ></dx-column>

    <dx-column
      :format="numberFormat"
      caption="Э/Э"
      dataField="value_ee"
      dataType="number"
      width="150px"
    ></dx-column>

    <dx-column
      :format="numberFormat"
      caption="Т/Э"
      dataField="value_te"
      dataType="number"
      width="150px"
    ></dx-column>

    <dx-column
      :format="numberFormat"
      caption="ОД Итого"
      dataField="od_total"
      dataType="number"
      width="150px"
    ></dx-column>

    <dx-column
      :format="numberFormat"
      caption="НД"
      dataField="value_nd"
      dataType="number"
      width="150px"
    ></dx-column>

    <dx-column
      caption="Коды статей фин.сист."
      dataField="sap_codes"
      dataType="string"
    ></dx-column>

    <template #filterTemplateDepartment>
      <mped-department-tree-component
        ref="departmentTree"
        :default-value="defaultValueDepartment"
        :validate-code="false"
        @value-changed="changedFilter($event, 'department')"
      />
    </template>

    <template #filterTemplatePeriod>
      <mped-calculation-period-component
        :default-period="'current'"
        :default-value="defaultValuePeriod"
        :show-clear-button="false"
        selection-mode="single"
        @value-changed="changedFilter($event, 'periods')"
      />
    </template>

  </dx-tree-list>
  <footer-grid :totalCount="totalCount" class="px-2"></footer-grid>
</template>

<script>
export default {
  name: 'mped-calculation-analysis-data-view',
};
</script>

<script setup>
import { onBeforeMount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  DxTreeList,
  DxColumn,
  DxScrolling,
  DxLoadPanel,
  DxSorting,
} from 'devextreme-vue/tree-list';

import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import MpedDepartmentTreeComponent from '@/views/mped/views/calculation/component/mped-department-tree-component.vue';
import MpedCalculationPeriodComponent
  from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { getCalculationAnalysisData, setCalculationDataMpep } from '@/views/mped/services/mped-calculation-api-service';
import { expandTree, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { getAiToolbar } from '@/components/utils/ai-toolbar-generator';
import { numberFormat } from '@/views/mped/services/mped-columns-service';
import { getLocalUserdata, initState, setLocalUserdata, hasEditablePermissionByUIRoute } from '@/service/local-userdata-service';
import { svgAiLibrary } from '@/service/svg-ai-library';

const treeListRef = ref();
const totalCount = ref(0);
const isExpand = ref(true);
const filter = ref({ periods: null, department: null });
const router = useRouter();
const isEditable = hasEditablePermissionByUIRoute(router);

let allIds = [];
let
  defaultValuePeriod = null,
  defaultValueDepartment = null,
  isFirstLoad = true;

const dataSource = getAiDataSource(
  {
    validationFn: () => filter.value.department && filter.value.periods,
    dataFn: getCalculationAnalysisData,
    paramsFn: () => Object.assign({}, {
      period: Number(filter.value.periods[0]),
      departmentId: filter.value.department.kod_direct,
    }),
    key: 'kod_spr_smet',
    onLoadedFn: (result) => {
      totalCount.value = result.data.length;
      allIds = result.data.filter(x => x.has_child).map(x => x.kod_spr_smet);
    },
  },
);

onBeforeMount(async () => {
  const state = getLocalUserdata(router);
  defaultValuePeriod = state?.filters?.periods;
  defaultValueDepartment = state?.filters?.department || -1;
});

onMounted(async () => {
  initState(router, treeListRef);
});

const getInstance = () => {
  return treeListRef.value.instance;
};

const reloadData = async () => {
  dataSource.reload();
};

const onToolbarPreparing = (e) => {
  getAiToolbar(e, {
    customs: [
      {
        location: 'before',
        template: 'filterTemplateDepartment',
      }, {
        location: 'before',
        template: 'filterTemplatePeriod',
      }, {
        visible: true,
        widget: 'dxButton',
        location: 'after',
        name: 'uploadFile',
        after: 'refresh',
        cssClass: 'toolbar-item-custom-style',
        options: {
          icon: svgAiLibrary['ai-upload-file-base'],
          hint: 'Загрузка факта по статьям сметы',
          onClick: saveDataMpep,
          disabled: !isEditable
        },
      }],
    expand: {
      event: (e) => {
        isExpand.value = !isExpand.value;
        expandTree({
          ids: allIds,
          dxInstance: getInstance(),
          expand: isExpand.value,
        });
      },
    },
    refresh: {
      event: async (e) => {
        await reloadData();
      },
    },

  });
};

const saveDataMpep = async () => {
  const result = await AiCustomConfirmDialog(
    'Загрузить факт по статьям сметы?',
    'Подтверждение',
    true,
    false,
  );
  if (result) {
    await setCalculationDataMpep(getParams());
  }
};

const getParams = () => {
  return Object.assign({}, {
    period: Number(filter.value.periods[0]),
    departmentId: filter.value.department.kod_direct,
  });
};

const changedFilter = (e, filed) => {
  filter.value[filed] = e;
  reloadData();
};

const onContentReady = e => {
  if (isFirstLoad && totalCount.value) {
    if (!getLocalUserdata(router))
      expandTree({
        ids: allIds,
        dxInstance: getInstance(),
      });
    isFirstLoad = false;
  }

  if (!isFirstLoad)
    setLocalUserdata(router, treeListRef, filter.value);
};

</script>

<style scoped>

</style>
